import { createContext, useCallback, useEffect, useRef, useState } from 'react'

import audioBento from '@/assets/audio/bento_notification_2023_v2.mp3'

const MAX_CONCURRENT_ONESHOTS = 5

type AudioContextProps = {
  playing: boolean
  canPlay: boolean
  play: () => void
  playOnce: () => void
  pause: () => void
}

export const AudioContext = createContext<AudioContextProps>(
  {} as AudioContextProps
)

type AudioProviderProps = {
  children: React.ReactNode
}

export function AudioProvider({ children }: AudioProviderProps) {
  const [playing, setPlaying] = useState(false)
  const [canPlay, setCanPlay] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const oneShotGenerationRef = useRef(0)
  const activeOneShotsRef = useRef<Set<HTMLAudioElement>>(new Set())
  const pendingOneShotsRef = useRef(0)
  const loopGenerationRef = useRef(0)
  const playbackUnlockedRef = useRef(false)
  const pendingLoopAfterUnlockRef = useRef(false)
  const runLoopPlayRef = useRef<() => void>(() => {})

  const tryPrimePlayback = useCallback((): Promise<boolean> => {
    const el = audioRef.current
    if (!el) return Promise.resolve(false)
    if (playbackUnlockedRef.current) return Promise.resolve(true)
    const prevMuted = el.muted
    el.muted = true
    const p = el.play()
    if (p === undefined) {
      el.muted = prevMuted
      return Promise.resolve(false)
    }
    return p
      .then(() => {
        el.pause()
        el.currentTime = 0
        el.muted = prevMuted
        playbackUnlockedRef.current = true
        return true
      })
      .catch(() => {
        el.muted = prevMuted
        return false
      })
  }, [])

  const play = useCallback(() => {
    const el = audioRef.current
    if (!el) return

    const run = () => {
      const currentGeneration = ++loopGenerationRef.current
      oneShotGenerationRef.current += 1
      pendingOneShotsRef.current = 0
      activeOneShotsRef.current.forEach((audio) => {
        audio.pause()
        audio.currentTime = 0
      })
      activeOneShotsRef.current.clear()
      el.onended = null
      el.loop = true
      const playPromise = el.play()
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            if (loopGenerationRef.current !== currentGeneration) return
            setPlaying(true)
            playbackUnlockedRef.current = true
          })
          .catch((error: { name?: string }) => {
            if (error?.name === 'NotAllowedError') {
              pendingLoopAfterUnlockRef.current = true
            }
          })
      }
    }

    runLoopPlayRef.current = run

    if (playbackUnlockedRef.current) {
      run()
      return
    }

    void tryPrimePlayback().then((primed) => {
      if (primed) {
        run()
      } else {
        pendingLoopAfterUnlockRef.current = true
      }
    })
  }, [tryPrimePlayback])

  const playOnce = useCallback(() => {
    if (!audioRef.current) return
    const generation = oneShotGenerationRef.current

    const playSingle = () => {
      if (oneShotGenerationRef.current !== generation) return

      if (activeOneShotsRef.current.size >= MAX_CONCURRENT_ONESHOTS) {
        pendingOneShotsRef.current += 1
        return
      }

      const oneShotAudio = new Audio(audioBento)
      activeOneShotsRef.current.add(oneShotAudio)

      const finishOneShot = () => {
        activeOneShotsRef.current.delete(oneShotAudio)
        if (oneShotGenerationRef.current !== generation) return
        if (pendingOneShotsRef.current > 0) {
          pendingOneShotsRef.current -= 1
          playSingle()
        }
      }

      oneShotAudio.onended = finishOneShot
      oneShotAudio.onerror = finishOneShot

      const runShot = () => {
        const playPromise = oneShotAudio.play()
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            finishOneShot()
          })
        }
      }

      if (playbackUnlockedRef.current) {
        runShot()
        return
      }

      const prevMuted = oneShotAudio.muted
      oneShotAudio.muted = true
      const prime = oneShotAudio.play()
      if (prime === undefined) {
        oneShotAudio.muted = prevMuted
        runShot()
        return
      }
      void prime
        .then(() => {
          oneShotAudio.pause()
          oneShotAudio.currentTime = 0
          oneShotAudio.muted = prevMuted
          runShot()
        })
        .catch(() => {
          oneShotAudio.muted = prevMuted
          runShot()
        })
    }

    playSingle()
  }, [])

  const pause = useCallback(() => {
    loopGenerationRef.current += 1
    pendingLoopAfterUnlockRef.current = false
    runLoopPlayRef.current = () => {}
    oneShotGenerationRef.current += 1
    pendingOneShotsRef.current = 0
    activeOneShotsRef.current.forEach((audio) => {
      audio.pause()
      audio.currentTime = 0
    })
    activeOneShotsRef.current.clear()
    if (audioRef.current) {
      const audio = audioRef.current
      audio.onended = null
      audio.pause()
      audio.currentTime = 0
    }
    setPlaying(false)
  }, [])

  useEffect(() => {
    const el = audioRef.current
    if (!el) return
    const onLoaded = () => {
      setCanPlay(true)
      void tryPrimePlayback()
    }
    if (el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      onLoaded()
    } else {
      el.addEventListener('loadeddata', onLoaded, { once: true })
    }
    return () => {
      el.removeEventListener('loadeddata', onLoaded)
    }
  }, [tryPrimePlayback])

  useEffect(() => {
    const onGesture = () => {
      void tryPrimePlayback().then((ok) => {
        if (ok && pendingLoopAfterUnlockRef.current) {
          pendingLoopAfterUnlockRef.current = false
          runLoopPlayRef.current()
        }
      })
    }
    window.addEventListener('pointerdown', onGesture, true)
    window.addEventListener('keydown', onGesture, true)
    return () => {
      window.removeEventListener('pointerdown', onGesture, true)
      window.removeEventListener('keydown', onGesture, true)
    }
  }, [tryPrimePlayback])

  return (
    <AudioContext.Provider
      value={{
        playing,
        canPlay,
        play,
        playOnce,
        pause,
      }}
    >
      {children}
      <audio src={audioBento} ref={audioRef} />
    </AudioContext.Provider>
  )
}
